import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  ArrowLeft,
  Mail,
  MapPin,
  Phone,
  Calendar,
  Globe,
  CheckCircle,
  XCircle,
  Eye,
  Trash2,
  Plus,
  Upload,
  FileText,
  FileSignature,
  Receipt,
  Download,
  ChevronLeft,
  ChevronRight,
  User,
  CreditCard,
  Package,
  Wallet,
  Search,
  X,
  Lock,
} from "lucide-react";
import {
  AdminPopup,
  AdminLayout,
  SideNav,
  CollapseButton,
  ConfirmDialog,
  Avatar,
  Pill,
  StatusBadge,
  Empty,
  Spinner,
  Skeleton,
  useSidebarCollapsed,
  formatDate,
  todayISO,
} from "../adminLayout";

/* ============================================================
   Menu configuration
   ============================================================ */
const NAV_GROUPS = [
  {
    label: "Account",
    items: [["profile", "Info", User]],
  },
  {
    label: "Finance",
    items: [
      ["subscribe", "Subscribe", CreditCard],
      ["payments", "Packs", Package],
      ["wallet", "Wallet", Wallet],
      ["invoices", "Invoices", Receipt],
    ],
  },
  {
    label: "Documents",
    items: [
      ["withdrawReports", "Reports", FileText],
      ["contracts", "Contracts", FileSignature],
    ],
  },
];

const TAB_TITLES = {
  profile: "Personal information",
  subscribe: "Subscription",
  payments: "Packs",
  wallet: "Wallet",
  invoices: "Invoices",
  withdrawReports: "Withdrawal reports",
  contracts: "Contracts",
};

/* ============================================================
   Small page components
   ============================================================ */
const Field = ({ icon: Icon, label, value }) => (
  <div className="adm-field">
    <Icon />
    <div className="adm-field-text">
      <span className="adm-field-label">{label}</span>
      <span className="adm-field-value">{value || "N/A"}</span>
    </div>
  </div>
);

/* Documents list (reports, contracts).
   The displayed date is the document date (reportDate) when it exists,
   otherwise the upload date (createdAt). */
const DocsPanel = ({ title, itemLabel, items, onAdd, onView, onDelete, emptyText }) => (
  <>
    <div className="adm-doc-toolbar">
      <h5>{title}</h5>
      <button className="adm-btn primary" onClick={onAdd}>
        <Plus /> Add
      </button>
    </div>
    {items.length === 0 ? (
      <Empty>{emptyText}</Empty>
    ) : (
      items.map((it, i) => (
        <div key={it._id} className="adm-doc">
          <div className="adm-doc-icon">
            <FileText size={18} />
          </div>
          <div className="adm-doc-text">
            <div className="adm-doc-title">
              {itemLabel} {i + 1}
            </div>
            <div className="adm-doc-date">{formatDate(it.reportDate || it.createdAt)}</div>
          </div>
          <button className="adm-icon-btn" title="View" onClick={() => onView(it._id)}>
            <Eye />
          </button>
          <button
            className="adm-icon-btn danger"
            title="Delete"
            onClick={() => onDelete(it._id)}
          >
            <Trash2 />
          </button>
        </div>
      ))
    )}
  </>
);

/* Invoice list (packs, general, subscription).
   - onReceipt: "receipt" button only if the invoice has a receipt (receiptPath) */
const InvoiceList = ({
  items,
  emptyText,
  onView,
  onDownload,
  onDelete,
  onReceipt,
  showPackName = true,
}) =>
  items.length === 0 ? (
    <Empty>{emptyText}</Empty>
  ) : (
    items.map((inv) => (
      <div key={inv._id} className="adm-doc">
        <div className="adm-doc-icon">
          <FileText size={18} />
        </div>
        <div className="adm-doc-text">
          <div className="adm-doc-title">
            {showPackName && inv.packName ? inv.packName : "Invoice"} · {formatDate(inv.invoiceDate)}
          </div>
          <div className="adm-doc-date">
            <StatusBadge status={inv.status} />
          </div>
        </div>
        {onReceipt && inv.receiptPath && (
          <button className="adm-icon-btn" title="View receipt" onClick={() => onReceipt(inv._id)}>
            <FileText />
          </button>
        )}
        <button className="adm-icon-btn" title="View invoice" onClick={() => onView(inv._id)}>
          <Eye />
        </button>
        <button className="adm-icon-btn" title="Download" onClick={() => onDownload(inv._id)}>
          <Download />
        </button>
        <button className="adm-icon-btn danger" title="Delete" onClick={() => onDelete(inv._id)}>
          <Trash2 />
        </button>
      </div>
    ))
  );

/* PDF upload modal.
   The date field only appears when `setDate` is provided
   (used for withdrawal reports, not for contracts). */
const UploadModal = ({
  title,
  file,
  setFile,
  uploading,
  onUpload,
  onClose,
  date,
  setDate,
  dateLabel = "Report date",
}) => (
  <div className="adm-modal-overlay" onMouseDown={() => !uploading && onClose()}>
    <div className="adm-modal-content" onMouseDown={(e) => e.stopPropagation()}>
      <div className="adm-modal-header">
        <h4>
          <Upload size={18} /> {title}
        </h4>
        <button className="adm-close-btn" onClick={onClose} disabled={uploading} aria-label="Close">
          ×
        </button>
      </div>
      <div className="adm-modal-body">
        <div className="adm-form">
          <label>
            <span>PDF file</span>
            <input
              type="file"
              accept=".pdf"
              disabled={uploading}
              onChange={(e) => setFile(e.target.files[0])}
            />
          </label>
          {file && (
            <div className="adm-file-preview">
              <p>
                Selected: <strong>{file.name}</strong>
              </p>
            </div>
          )}
          {setDate && (
            <label>
              <span>{dateLabel}</span>
              <input
                type="date"
                value={date}
                disabled={uploading}
                onChange={(e) => setDate(e.target.value)}
              />
            </label>
          )}
          <div className="adm-upload-buttons">
            <button
              className="adm-upload-btn"
              onClick={onUpload}
              disabled={uploading || !file || (setDate && !date)}
            >
              {uploading ? "Uploading..." : "Upload"}
            </button>
            <button className="adm-cancel-btn" onClick={onClose} disabled={uploading}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
);

/* Invoice upload form: replaces the tab content,
   then the parent content comes back after the upload or "Cancel". */
const InvoiceUploadForm = ({
  title,
  subtitle,
  file,
  setFile,
  date,
  setDate,
  status,
  setStatus,
  uploading,
  onUpload,
  onCancel,
}) => (
  <div className="adm-upload-card">
    <div className="adm-doc-toolbar" style={{ marginBottom: subtitle ? 4 : 14 }}>
      <h5 style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Upload size={16} /> {title}
      </h5>
    </div>
    {subtitle && (
      <div className="adm-doc-date" style={{ marginBottom: 14 }}>
        {subtitle}
      </div>
    )}

    <div className="adm-form">
      <label>
        <span>PDF file</span>
        <input
          type="file"
          accept=".pdf"
          disabled={uploading}
          onChange={(e) => setFile(e.target.files[0])}
        />
      </label>
      {file && (
        <div className="adm-file-preview">
          <p>
            Selected: <strong>{file.name}</strong>
          </p>
        </div>
      )}
      <label>
        <span>Invoice date</span>
        <input
          type="date"
          value={date}
          disabled={uploading}
          onChange={(e) => setDate(e.target.value)}
        />
      </label>
      <label>
        <span>Status</span>
        <select value={status} disabled={uploading} onChange={(e) => setStatus(e.target.value)}>
          <option value="pending">Pending</option>
          <option value="paid">Paid</option>
        </select>
      </label>
      <div className="adm-upload-buttons">
        <button className="adm-upload-btn" onClick={onUpload} disabled={uploading || !file || !date}>
          {uploading ? "Uploading..." : "Upload"}
        </button>
        <button className="adm-cancel-btn" onClick={onCancel} disabled={uploading}>
          Cancel
        </button>
      </div>
    </div>
  </div>
);

/* Wallet: points are loaded by Profile (shared with the Info tab summary) */
const WalletFullView = ({ userId, points, loading, API_URL, onUpdated }) => {
  const [newPointsValue, setNewPointsValue] = useState("");
  const [updating, setUpdating] = useState(false);

  const updateValidPoints = async () => {
    const value = Number(newPointsValue);

    if (isNaN(value) || value < 0) {
      toast.error("Please enter a positive number");
      return;
    }
    if (value === points) {
      toast.info("No changes detected");
      setNewPointsValue("");
      return;
    }

    setUpdating(true);
    const token = localStorage.getItem("token");

    try {
      await axios.put(
        `${API_URL}/admin-update-valid-points`,
        { userId, newValidPoints: value },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onUpdated(value);
      setNewPointsValue("");
      toast.success(`Valid points updated successfully: ${value.toLocaleString()} points`);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update points");
    } finally {
      setUpdating(false);
    }
  };

  if (loading) return <Spinner>Loading wallet...</Spinner>;

  const diff = Number(newPointsValue) - points;

  return (
    <>
      <div className="adm-balance">
        <div className="adm-balance-label">Valid points</div>
        <div className="adm-balance-value">
          {points.toLocaleString()}
          <span className="adm-balance-unit">pts</span>
        </div>
      </div>

      <h4 className="adm-section-title">Adjust balance (admin)</h4>
      <div className="adm-adjust">
        <input
          type="number"
          min="0"
          className="adm-search"
          placeholder="New value, e.g. 5000"
          value={newPointsValue}
          onChange={(e) => setNewPointsValue(e.target.value)}
        />
        <button
          className="adm-btn primary"
          onClick={updateValidPoints}
          disabled={updating || !newPointsValue}
        >
          {updating ? "Saving..." : "Apply"}
        </button>
      </div>

      {newPointsValue && diff !== 0 && (
        <div className="adm-preview">
          New balance: <strong>{Number(newPointsValue).toLocaleString()}</strong> pts{" "}
          <span className={diff > 0 ? "up" : "down"}>
            ({diff > 0 ? "+" : ""}
            {diff.toLocaleString()})
          </span>
        </div>
      )}
    </>
  );
};

/* ============================================================
   Main component
   ============================================================ */
const Profile = ({ onClose }) => {
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

  const [collapsed, toggleCollapsed] = useSidebarCollapsed();

  /* List + selection */
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("all");
  const [selectedUser, setSelectedUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [viewMode, setViewMode] = useState("profile");

  /* Loading states */
  const [isLoading, setIsLoading] = useState(false); // users list
  const [profileLoading, setProfileLoading] = useState(false);
  const [tabLoading, setTabLoading] = useState(false); // tab data (preloaded)

  /* Selected user's data */
  const [paymentsData, setPaymentsData] = useState(null);
  const [packInvoices, setPackInvoices] = useState([]);
  const [subInvoices, setSubInvoices] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [reports, setReports] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [walletPoints, setWalletPoints] = useState(0);
  const [walletLoading, setWalletLoading] = useState(true);

  /* Interface */
  const [selectedPack, setSelectedPack] = useState(null);
  const [invoiceSection, setInvoiceSection] = useState("receipts");
  const [confirm, setConfirm] = useState(null);

  /* Uploads */
  const [showUpload, setShowUpload] = useState(false);
  const [file, setFile] = useState(null);
  const [reportDate, setReportDate] = useState(todayISO());
  const [uploading, setUploading] = useState(false);
  const [showUploadContract, setShowUploadContract] = useState(false);
  const [contractFile, setContractFile] = useState(null);
  const [uploadingContract, setUploadingContract] = useState(false);
  const [uploadInvoiceType, setUploadInvoiceType] = useState(null);
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [invoiceDate, setInvoiceDate] = useState(todayISO());
  const [invoiceStatus, setInvoiceStatus] = useState("pending");
  const [uploadingInvoice, setUploadingInvoice] = useState(false);

  /* Fetches the user's invoices for a given type ("pack", "general" or "subscription") */
  const fetchInvoicesByType = async (type, userId = selectedUser._id) => {
    const res = await axios.get(`${API_URL}/invoices/user/${userId}`, {
      params: { type },
      headers: authHeaders(),
    });
    return res.data.invoices || [];
  };

  /* Confirmation request (replaces window.confirm) */
  const ask = (options) => setConfirm(options);
  const runConfirm = async () => {
    const current = confirm;
    setConfirm(null);
    if (current?.onConfirm) await current.onConfirm();
  };

  /* ---------- Users list ---------- */
  useEffect(() => {
    const fetchUsers = async () => {
      if (!localStorage.getItem("token")) return;

      setIsLoading(true);
      try {
        const res = await axios.get(`${API_URL}/profiles/users-with-status`, {
          headers: authHeaders(),
        });
        setUsers(res.data || []);
      } catch (err) {
        toast.error("Error loading users");
      } finally {
        setIsLoading(false);
      }
    };
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------- Selected user's profile ---------- */
  useEffect(() => {
    if (!selectedUser?._id) return;
    let cancelled = false;

    const fetchProfile = async () => {
      setProfileLoading(true);
      try {
        const res = await axios.get(`${API_URL}/profiles/${selectedUser._id}`, {
          headers: authHeaders(),
        });
        if (!cancelled) setProfile(res.data);
      } catch (err) {
        if (!cancelled) toast.error("Error loading profile");
      } finally {
        if (!cancelled) setProfileLoading(false);
      }
    };
    fetchProfile();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser]);

  /* ---------- Wallet (shared with the Info tab summary) ---------- */
  const walletUserId = profile?.userId || profile?._id;

  useEffect(() => {
    if (!walletUserId) return;
    let cancelled = false;

    const loadWallet = async () => {
      setWalletLoading(true);
      try {
        const res = await axios.get(`${API_URL}/wallets/${walletUserId}`, {
          headers: authHeaders(),
        });
        if (!cancelled) setWalletPoints(res.data.validPoints || 0);
      } catch (err) {
        if (!cancelled) toast.error("Error loading wallet");
      } finally {
        if (!cancelled) setWalletLoading(false);
      }
    };
    loadWallet();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletUserId]);

  /* ---------- Preload all tabs (feeds the counters) ---------- */
  useEffect(() => {
    if (!selectedUser?._id) return;
    const userId = selectedUser._id;
    let cancelled = false;

    const preload = async () => {
      setTabLoading(true);
      const [pay, pack, sub, gen, rep, con] = await Promise.allSettled([
        axios.get(`${API_URL}/payments/count-by-pack/${userId}`, { headers: authHeaders() }),
        fetchInvoicesByType("pack", userId),
        fetchInvoicesByType("subscription", userId),
        fetchInvoicesByType("general", userId),
        axios.get(`${API_URL}/reportsWithdraw/user/${userId}`, { headers: authHeaders() }),
        axios.get(`${API_URL}/contracts/user/${userId}`, { headers: authHeaders() }),
      ]);
      if (cancelled) return;

      if (pay.status === "fulfilled") setPaymentsData(pay.value.data.packCounts || {});
      else toast.error("Error loading payments");

      if (pack.status === "fulfilled") setPackInvoices(pack.value);
      if (sub.status === "fulfilled") setSubInvoices(sub.value);
      if (gen.status === "fulfilled") setInvoices(gen.value);
      if (rep.status === "fulfilled") setReports(rep.value.data.reports || []);
      if (con.status === "fulfilled") setContracts(con.value.data.contracts || []);

      if ([pack, sub, gen].some((r) => r.status === "rejected")) {
        toast.error("Error loading invoices");
      }
      if (rep.status === "rejected") toast.error("Error loading reports");
      if (con.status === "rejected") toast.error("Error loading contracts");

      setTabLoading(false);
    };
    preload();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser]);

  /* ---------- Open a PDF in a new tab ---------- */
  const openBlob = async (url, errorMessage, revokeAfter = 60000) => {
    try {
      const response = await axios.get(url, { headers: authHeaders(), responseType: "blob" });
      const type = response.headers["content-type"] || "application/pdf";
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type }));
      window.open(blobUrl, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), revokeAfter);
    } catch (error) {
      console.error(errorMessage, error);
      toast.error(error.response?.status === 404 ? "File not found" : errorMessage);
    }
  };

  /* ---------- Reports ---------- */
  const handleViewReport = (id) =>
    openBlob(`${API_URL}/reportsWithdraw/view/${id}`, "Unable to open the report");

  const handleDeleteReport = (id) =>
    ask({
      title: "Delete this report?",
      message: "The report will be permanently deleted.",
      confirmLabel: "Delete",
      onConfirm: async () => {
        try {
          await axios.delete(`${API_URL}/reportsWithdraw/${id}`, { headers: authHeaders() });
          setReports((prev) => prev.filter((r) => r._id !== id));
          toast.success("Report deleted successfully");
        } catch (err) {
          toast.error("Failed to delete report");
        }
      },
    });

  /* Closes the report upload modal and resets its fields */
  const closeReportUpload = () => {
    setShowUpload(false);
    setFile(null);
    setReportDate(todayISO());
  };

  const handleUploadReport = async () => {
    if (!file) return toast.error("Please select a PDF file");
    if (!reportDate) return toast.error("Please select a date");

    const formData = new FormData();
    formData.append("pdf", file);
    formData.append("userId", selectedUser._id);
    formData.append("reportDate", reportDate);

    setUploading(true);
    try {
      await axios.post(`${API_URL}/reportsWithdraw/upload`, formData, { headers: authHeaders() });
      toast.success("Report uploaded successfully!");
      closeReportUpload();

      const res = await axios.get(`${API_URL}/reportsWithdraw/user/${selectedUser._id}`, {
        headers: authHeaders(),
      });
      setReports(res.data.reports || []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  /* ---------- Contracts ---------- */
  const handleViewContract = async (contractId) => {
    const token = localStorage.getItem("token");

    if (!token || token.length < 20 || token.trim() === "") {
      toast.error("Authentication required - please log in again");
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/contracts/view/${contractId}`, {
        headers: authHeaders(),
        responseType: "blob",
      });
      const blobUrl = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" })
      );
      window.open(blobUrl, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
    } catch (error) {
      console.error("Error opening contract:", error);
      toast.error(error.response?.data?.message || "Unable to open the contract");

      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem("token");
        toast.warn("Session expired. Please log in again.");
      }
    }
  };

  const handleDeleteContract = (id) =>
    ask({
      title: "Delete this contract?",
      message: "The contract will be permanently deleted.",
      confirmLabel: "Delete",
      onConfirm: async () => {
        try {
          await axios.delete(`${API_URL}/contracts/${id}`, { headers: authHeaders() });
          setContracts((prev) => prev.filter((c) => c._id !== id));
          toast.success("Contract deleted successfully");
        } catch (err) {
          toast.error("Failed to delete contract");
        }
      },
    });

  const handleUploadContract = async () => {
    if (!contractFile) return toast.error("Please select a PDF file");

    const formData = new FormData();
    formData.append("pdf", contractFile);
    formData.append("userId", selectedUser._id);

    setUploadingContract(true);
    try {
      await axios.post(`${API_URL}/contracts/upload`, formData, { headers: authHeaders() });
      toast.success("Contract added successfully!");
      setContractFile(null);
      setShowUploadContract(false);

      const res = await axios.get(`${API_URL}/contracts/user/${selectedUser._id}`, {
        headers: authHeaders(),
      });
      setContracts(res.data.contracts || []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Upload failed");
    } finally {
      setUploadingContract(false);
    }
  };

  /* ---------- Invoices: view / download / receipt / delete / upload ---------- */
  const handleViewInvoice = (id) =>
    openBlob(`${API_URL}/invoices/view/${id}`, "Unable to open the invoice");

  const handleOpenReceipt = (id) =>
    openBlob(`${API_URL}/invoices/receipt/${id}`, "Unable to open the receipt");

  const handleDownloadInvoice = async (invoiceId) => {
    try {
      const response = await axios.get(`${API_URL}/invoices/download/${invoiceId}`, {
        headers: authHeaders(),
        responseType: "blob",
      });
      const blobUrl = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" })
      );
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `invoice-${invoiceId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Error downloading invoice:", error);
      toast.error("Unable to download the invoice");
    }
  };

  const handleDeleteInvoice = (id, type) =>
    ask({
      title: "Delete this invoice?",
      message: "The invoice will be permanently deleted.",
      confirmLabel: "Delete",
      onConfirm: async () => {
        try {
          await axios.delete(`${API_URL}/invoices/${id}`, { headers: authHeaders() });
          const setter =
            type === "pack" ? setPackInvoices : type === "subscription" ? setSubInvoices : setInvoices;
          setter((prev) => prev.filter((inv) => inv._id !== id));
          toast.success("Invoice deleted successfully");
        } catch (err) {
          toast.error(err.response?.data?.message || "Failed to delete invoice");
        }
      },
    });

  const closeInvoiceUpload = () => {
    setUploadInvoiceType(null);
    setInvoiceFile(null);
    setInvoiceDate(todayISO());
    setInvoiceStatus("pending");
  };

  const handleUploadInvoice = async () => {
    if (!invoiceFile) return toast.error("Please select a PDF file");
    if (!invoiceDate) return toast.error("Please select a date");

    const type = uploadInvoiceType; // "pack", "general" or "subscription"
    const formData = new FormData();
    formData.append("pdf", invoiceFile);
    formData.append("invoiceDate", invoiceDate);
    formData.append("status", invoiceStatus);
    formData.append("invoiceType", type);
    if (type === "pack" && selectedPack) formData.append("packName", selectedPack);

    setUploadingInvoice(true);
    try {
      await axios.post(`${API_URL}/invoices/upload/${selectedUser._id}`, formData, {
        headers: authHeaders(),
      });
      toast.success("Invoice uploaded successfully!");
      closeInvoiceUpload();

      const list = await fetchInvoicesByType(type);
      if (type === "pack") setPackInvoices(list);
      else if (type === "subscription") setSubInvoices(list);
      else setInvoices(list);
    } catch (err) {
      toast.error(err.response?.data?.message || "Upload failed");
    } finally {
      setUploadingInvoice(false);
    }
  };

  /* ---------- Block / Unblock ---------- */
  const toggleBlock = async () => {
    try {
      await axios.put(`${API_URL}/users/${selectedUser._id}/block`, {}, { headers: authHeaders() });
      setProfile((prev) => ({ ...prev, isBlocked: !prev.isBlocked }));
      toast.success(profile?.isBlocked ? "User unblocked" : "User blocked");
    } catch (err) {
      toast.error("Failed to update block status");
    }
  };

  const handleBlockClick = () => {
    if (profile?.isBlocked) return toggleBlock();
    ask({
      title: "Block this user?",
      message: "The user will lose access until you unblock them.",
      confirmLabel: "Block user",
      onConfirm: toggleBlock,
    });
  };

  /* ---------- Navigation ---------- */
  const resetUserData = () => {
    setProfile(null);
    setPaymentsData(null);
    setPackInvoices([]);
    setSubInvoices([]);
    setInvoices([]);
    setReports([]);
    setContracts([]);
    setWalletPoints(0);
    setWalletLoading(true);
    setSelectedPack(null);
    setInvoiceSection("receipts");
    setShowUploadContract(false);
    setContractFile(null);
    closeReportUpload();
    closeInvoiceUpload();
  };

  const handleBack = () => {
    resetUserData();
    setSelectedUser(null);
    setViewMode("profile");
  };

  const goToUser = (user) => {
    if (!user || user._id === selectedUser?._id) return;
    resetUserData();
    setSelectedUser(user);
  };

  /* Go back one level inside the content area */
  const handleInnerBack = () => {
    if (uploadInvoiceType) return closeInvoiceUpload();
    if (viewMode === "payments" && selectedPack) return setSelectedPack(null);
  };

  const hasInnerBack = Boolean(uploadInvoiceType || (viewMode === "payments" && selectedPack));

  /* Escape key: goes up one level at a time, then closes the popup */
  const handleEscape = () => {
    if (confirm) return setConfirm(null);
    if (showUpload) return closeReportUpload();
    if (showUploadContract) {
      setShowUploadContract(false);
      return setContractFile(null);
    }
    if (!selectedUser) return onClose?.();
    if (hasInnerBack) return handleInnerBack();
    handleBack();
  };

  /* Filter (first name, last name, email) + alphabetical sort */
  const filteredUsers = users
    .filter((user) => {
      const search = searchTerm.trim().toLowerCase();
      const haystack = `${user.name || ""} ${user.surname || ""} ${user.email || ""}`.toLowerCase();
      return (
        haystack.includes(search) &&
        (filter === "all" ||
          (filter === "verified" && user.isVerified) ||
          (filter === "notVerified" && !user.isVerified))
      );
    })
    .sort((a, b) => `${a.name || ""} ${a.surname || ""}`.localeCompare(`${b.name || ""} ${b.surname || ""}`));

  /* Counters displayed in the chips */
  const filterCounts = {
    all: users.length,
    verified: users.filter((u) => u.isVerified).length,
    notVerified: users.filter((u) => !u.isVerified).length,
  };

  /* Users you can switch between (previous / next profile) */
  const navUsers = (
    selectedUser && filteredUsers.some((u) => u._id === selectedUser._id) ? filteredUsers : users
  ).filter((u) => u.isVerified);
  const userIndex = selectedUser ? navUsers.findIndex((u) => u._id === selectedUser._id) : -1;

  /* Derived values */
  const receipts = invoices.filter((inv) => inv.receiptPath);

  const counts = {
    subscribe: subInvoices.length,
    payments: Object.keys(paymentsData || {}).length,
    invoices: invoices.length,
    withdrawReports: reports.length,
    contracts: contracts.length,
  };

  const totalPacks = Object.values(paymentsData || {}).reduce(
    (sum, p) => sum + (p.totalQuantity || 0),
    0
  );

  const summaryTiles = [
    ["wallet", walletLoading ? "…" : walletPoints.toLocaleString(), "Valid points"],
    ["payments", tabLoading ? "…" : totalPacks, "Packs purchased"],
    [
      "subscribe",
      tabLoading ? "…" : packInvoices.length + subInvoices.length + invoices.length,
      "Invoices",
    ],
    ["withdrawReports", tabLoading ? "…" : reports.length + contracts.length, "Documents"],
  ];

  const uploadSubtitle =
    uploadInvoiceType === "pack"
      ? `Pack: ${selectedPack}`
      : uploadInvoiceType === "subscription"
      ? "Subscription invoice"
      : null;

  const blockButton = (
    <button
      className={`adm-btn block ${profile?.isBlocked ? "success" : "danger"}`}
      onClick={handleBlockClick}
      disabled={!profile}
      title={profile?.isBlocked ? "Unblock user" : "Block user"}
    >
      {profile?.isBlocked ? <CheckCircle /> : <XCircle />}
      <span className="adm-btn-text">{profile?.isBlocked ? "Unblock user" : "Block user"}</span>
    </button>
  );

  const confirmDialog = (
    <ConfirmDialog
      open={Boolean(confirm)}
      title={confirm?.title}
      message={confirm?.message}
      confirmLabel={confirm?.confirmLabel}
      onConfirm={runConfirm}
      onCancel={() => setConfirm(null)}
    />
  );

  /* ============================================================
     Render: screen 1 → users list
     ============================================================ */
  if (!selectedUser) {
    return (
      <AdminPopup onClose={onClose} onEscape={handleEscape}>
        <div className="adm-listview">
          <div className="adm-topbar">
            <button className="adm-back" onClick={onClose} aria-label="Close">
              <ArrowLeft /> Back
            </button>
            <h3>Users</h3>
            <span className="adm-topbar-count">{filterCounts.all} total</span>
          </div>

          {/* Fixed area: search + filters */}
          <div className="adm-list-tools">
            <div className="adm-searchbox">
              <Search />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
              />
              {searchTerm && (
                <button
                  className="adm-searchbox-clear"
                  onClick={() => setSearchTerm("")}
                  aria-label="Clear search"
                >
                  <X />
                </button>
              )}
            </div>

            <div className="adm-filters">
              {[
                ["all", "All"],
                ["verified", "Verified"],
                ["notVerified", "Not verified"],
              ].map(([key, label]) => (
                <button
                  key={key}
                  className={`adm-chip ${filter === key ? "active" : ""}`}
                  onClick={() => setFilter(key)}
                >
                  {label}
                  <span className="adm-chip-count">{filterCounts[key]}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Scrollable area */}
          <div className="adm-body">
            {isLoading ? (
              <Skeleton rows={5} />
            ) : filteredUsers.length > 0 ? (
              <div className="adm-table">
                <div className="adm-table-head">
                  <span>User</span>
                  <span>Status</span>
                </div>
                {filteredUsers.map((user) => (
                  <div
                    key={user._id}
                    className={`adm-urow ${user.isVerified ? "clickable" : "locked"}`}
                    onClick={() => user.isVerified && setSelectedUser(user)}
                    title={user.isVerified ? "" : "Verify this user to open the profile"}
                  >
                    <Avatar user={user} />
                    <div className="adm-urow-text">
                      <div className="adm-urow-name">
                        {user.name} {user.surname}
                      </div>
                      <div className="adm-urow-sub">{user.email || "No email"}</div>
                    </div>
                    <Pill tone={user.isVerified ? "ok" : "bad"}>
                      {user.isVerified ? "Verified" : "Not verified"}
                    </Pill>
                    {user.isVerified ? (
                      <ChevronRight className="adm-urow-arrow" />
                    ) : (
                      <Lock className="adm-urow-arrow" />
                    )}
                  </div>
                ))}
                <div className="adm-table-foot">
                  {filteredUsers.length} user{filteredUsers.length === 1 ? "" : "s"}
                </div>
              </div>
            ) : (
              <Empty>
                <Search size={20} />
                No users match your search.
              </Empty>
            )}
          </div>
        </div>
      </AdminPopup>
    );
  }

  /* ============================================================
     Render: screen 2 → sidebar + content
     ============================================================ */
  const side = (
    <>
      <button className="adm-back" onClick={handleBack} title="All users">
        <ArrowLeft />
        <span className="adm-hide-collapsed">All users</span>
      </button>

      <div className="adm-user">
        <Avatar user={profile || selectedUser} large />
        <h4 className="adm-user-name">
          {profile?.name || selectedUser.name} {profile?.surname || selectedUser.surname}
        </h4>
        <Pill tone={profile?.isBlocked ? "bad" : "ok"}>{profile?.isBlocked ? "Blocked" : "Active"}</Pill>

        {navUsers.length > 1 && userIndex >= 0 && (
          <div className="adm-user-switch">
            <button
              className="adm-icon-btn sm"
              title="Previous user"
              aria-label="Previous user"
              onClick={() => goToUser(navUsers[(userIndex - 1 + navUsers.length) % navUsers.length])}
            >
              <ChevronLeft />
            </button>
            <span>
              {userIndex + 1} / {navUsers.length}
            </span>
            <button
              className="adm-icon-btn sm"
              title="Next user"
              aria-label="Next user"
              onClick={() => goToUser(navUsers[(userIndex + 1) % navUsers.length])}
            >
              <ChevronRight />
            </button>
          </div>
        )}
      </div>

      <SideNav
        groups={NAV_GROUPS}
        active={viewMode}
        counts={counts}
        collapsed={collapsed}
        onSelect={(key) => {
          setViewMode(key);
          setSelectedPack(null);
          closeInvoiceUpload();
        }}
      />

      <div className="adm-side-foot">
        <CollapseButton collapsed={collapsed} onToggle={toggleCollapsed} />
        {blockButton}
      </div>
    </>
  );

  const content = uploadInvoiceType ? (
    <InvoiceUploadForm
      title="New invoice"
      subtitle={uploadSubtitle}
      file={invoiceFile}
      setFile={setInvoiceFile}
      date={invoiceDate}
      setDate={setInvoiceDate}
      status={invoiceStatus}
      setStatus={setInvoiceStatus}
      uploading={uploadingInvoice}
      onUpload={handleUploadInvoice}
      onCancel={closeInvoiceUpload}
    />
  ) : viewMode === "profile" ? (
    profileLoading ? (
      <Skeleton rows={5} />
    ) : (
      <>
        <div className="adm-summary">
          {summaryTiles.map(([target, value, label]) => (
            <button key={label} className="adm-tile" onClick={() => setViewMode(target)}>
              <div className="adm-tile-value">{value}</div>
              <div className="adm-tile-label">{label}</div>
            </button>
          ))}
        </div>

        <div className="adm-fields">
          <Field icon={Mail} label="Email" value={profile?.email} />
          <Field icon={MapPin} label="Address" value={profile?.address} />
          <Field icon={Phone} label="Phone" value={profile?.mobile || profile?.phoneNumber} />
          <Field
            icon={Calendar}
            label="Date of birth"
            value={profile?.dateOfBirth && formatDate(profile.dateOfBirth)}
          />
          <Field icon={Globe} label="Country" value={profile?.country} />
        </div>

        {/* On mobile the sidebar has no footer: the button is here */}
        <div className="adm-danger-zone adm-mobile-only">{blockButton}</div>
      </>
    )
  ) : viewMode === "wallet" ? (
    <WalletFullView
      userId={walletUserId}
      points={walletPoints}
      loading={walletLoading || !walletUserId}
      API_URL={API_URL}
      onUpdated={setWalletPoints}
    />
  ) : tabLoading ? (
    <Skeleton rows={4} />
  ) : viewMode === "subscribe" ? (
    <>
      <div className="adm-doc-toolbar">
        <h5>Subscription invoices</h5>
        <button className="adm-btn primary" onClick={() => setUploadInvoiceType("subscription")}>
          <Plus /> Add
        </button>
      </div>
      <InvoiceList
        items={subInvoices}
        emptyText="No invoices yet. Add the first one."
        showPackName={false}
        onView={handleViewInvoice}
        onDownload={handleDownloadInvoice}
        onDelete={(id) => handleDeleteInvoice(id, "subscription")}
      />
    </>
  ) : viewMode === "payments" ? (
    selectedPack ? (
      <>
        <div className="adm-pack">
          <img
            className="adm-pack-img"
            src={`/images/packs/unlimitedBonus/${selectedPack.replace(/ /g, "_")}.jpg`}
            alt=""
            onError={(e) => {
              e.currentTarget.style.visibility = "hidden";
            }}
          />
          <div className="adm-pack-info">
            <span className="adm-pack-name">{selectedPack}</span>
            <span className="adm-pack-qty">Purchased</span>
          </div>
          <span className="adm-badge-count">×{paymentsData?.[selectedPack]?.totalQuantity ?? 0}</span>
        </div>

        <div className="adm-doc-toolbar" style={{ marginTop: 20 }}>
          <h5>Invoices</h5>
          <button className="adm-btn primary" onClick={() => setUploadInvoiceType("pack")}>
            <Plus /> Add
          </button>
        </div>
        <InvoiceList
          items={packInvoices.filter((inv) => inv.packName === selectedPack)}
          emptyText="No invoices for this pack yet. Add the first one."
          showPackName={false}
          onView={handleViewInvoice}
          onDownload={handleDownloadInvoice}
          onReceipt={handleOpenReceipt}
          onDelete={(id) => handleDeleteInvoice(id, "pack")}
        />
      </>
    ) : Object.keys(paymentsData || {}).length > 0 ? (
      <div className="adm-packs">
        {Object.entries(paymentsData).map(([packName, data]) => {
          const count = packInvoices.filter((inv) => inv.packName === packName).length;
          return (
            <div
              key={packName}
              className="adm-pack clickable"
              role="button"
              tabIndex={0}
              onClick={() => setSelectedPack(packName)}
              onKeyDown={(e) => e.key === "Enter" && setSelectedPack(packName)}
            >
              <img
                className="adm-pack-img"
                src={`/images/packs/unlimitedBonus/${packName.replace(/ /g, "_")}.jpg`}
                alt=""
                onError={(e) => {
                  e.currentTarget.style.visibility = "hidden";
                }}
              />
              <div className="adm-pack-info">
                <span className="adm-pack-name">{packName}</span>
                <span className="adm-pack-qty">
                  Purchased · {count} invoice{count === 1 ? "" : "s"}
                </span>
              </div>
              <span className="adm-badge-count">×{data.totalQuantity}</span>
              <ChevronRight size={16} style={{ opacity: 0.5, flex: "none" }} />
            </div>
          );
        })}
      </div>
    ) : (
      <Empty>No approved packs yet.</Empty>
    )
  ) : viewMode === "invoices" ? (
    <>
      <div className="adm-filters" style={{ marginTop: 0 }}>
        {[
          ["receipts", `Receipts (${receipts.length})`],
          ["invoiceFiles", `Invoices (${invoices.length})`],
        ].map(([key, label]) => (
          <button
            key={key}
            className={`adm-chip ${invoiceSection === key ? "active" : ""}`}
            onClick={() => setInvoiceSection(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {invoiceSection === "receipts" ? (
        <>
          <div className="adm-doc-toolbar">
            <h5>Invoice receipts</h5>
          </div>
          {receipts.length === 0 ? (
            <Empty>No receipts submitted for this user yet.</Empty>
          ) : (
            receipts.map((inv) => (
              <div key={inv._id} className="adm-doc">
                <div className="adm-doc-icon">
                  <FileText size={18} />
                </div>
                <div className="adm-doc-text">
                  <div className="adm-doc-title">Receipt · {formatDate(inv.invoiceDate)}</div>
                  <div className="adm-doc-date">
                    <StatusBadge status={inv.status} />
                  </div>
                </div>
                <button
                  className="adm-icon-btn"
                  title="View receipt"
                  onClick={() => handleOpenReceipt(inv._id)}
                >
                  <Eye />
                </button>
              </div>
            ))
          )}
        </>
      ) : (
        <>
          <div className="adm-doc-toolbar">
            <h5>Invoices</h5>
            <button className="adm-btn primary" onClick={() => setUploadInvoiceType("general")}>
              <Plus /> Add
            </button>
          </div>
          <InvoiceList
            items={invoices}
            emptyText="No invoices yet. Add the first one."
            onView={handleViewInvoice}
            onDownload={handleDownloadInvoice}
            onDelete={(id) => handleDeleteInvoice(id, "general")}
          />
        </>
      )}
    </>
  ) : viewMode === "withdrawReports" ? (
    <>
      <DocsPanel
        title="Withdrawal reports"
        itemLabel="Report"
        items={reports}
        onAdd={() => setShowUpload(true)}
        onView={handleViewReport}
        onDelete={handleDeleteReport}
        emptyText="No withdrawal reports yet. Add the first one."
      />
      {showUpload && (
        <UploadModal
          title="Add withdrawal report"
          file={file}
          setFile={setFile}
          date={reportDate}
          setDate={setReportDate}
          dateLabel="Report date"
          uploading={uploading}
          onUpload={handleUploadReport}
          onClose={closeReportUpload}
        />
      )}
    </>
  ) : viewMode === "contracts" ? (
    <>
      <DocsPanel
        title="Contracts"
        itemLabel="Contract"
        items={contracts}
        onAdd={() => setShowUploadContract(true)}
        onView={handleViewContract}
        onDelete={handleDeleteContract}
        emptyText="No contracts yet. Add the first one."
      />
      {showUploadContract && (
        <UploadModal
          title="Add contract"
          file={contractFile}
          setFile={setContractFile}
          uploading={uploadingContract}
          onUpload={handleUploadContract}
          onClose={() => {
            setShowUploadContract(false);
            setContractFile(null);
          }}
        />
      )}
    </>
  ) : null;

  return (
    <AdminPopup onClose={onClose} onEscape={handleEscape}>
      <AdminLayout
        collapsed={collapsed}
        side={side}
        title={TAB_TITLES[viewMode]}
        headStart={
          hasInnerBack && (
            <button className="adm-icon-btn" onClick={handleInnerBack} title="Back" aria-label="Back">
              <ArrowLeft />
            </button>
          )
        }
      >
        {content}
      </AdminLayout>
      {confirmDialog}
    </AdminPopup>
  );
};

export default Profile;
